/**
 * Utility to convert technical error messages into user-friendly messages
 */

// Map of technical error messages to user-friendly messages
const ERROR_MESSAGES = {
  // Authentication errors
  'Invalid credentials': 'Invalid email or password. Please try again.',
  'User not found': 'No account found with this email address.',
  'Invalid token': 'Your session has expired. Please log in again.',
  'Token expired': 'Your session has expired. Please log in again.',
  
  // Validation errors
  'Password must be at least 6 characters long': 'Password must be at least 6 characters long',
  'Email is required': 'Please enter your email address',
  'Password is required': 'Please enter your password',
  'First name and last name are required': 'Please enter your first and last name',
  'Passwords do not match': 'Passwords do not match',
  
  // Network/Connection errors
  'Network Error': 'Unable to connect to the server. Please check your internet connection.',
  'Failed to connect to server': 'Unable to connect to the server. Please check your internet connection.',
  'Network error occurred': 'Unable to connect to the server. Please check your internet connection.',
  'timeout': 'The request took too long. Please try again.',
  'ECONNREFUSED': 'Unable to connect to the server. Please try again later.',
  'ENOTFOUND': 'Unable to connect to the server. Please check your internet connection.',
  
  // Server errors
  'Internal Server Error': 'Something went wrong on our end. Please try again later.',
  'Server error': 'Something went wrong on our end. Please try again later.',
  'MongoDB': 'Our database is temporarily unavailable. Please try again in a few moments.',
  'Database': 'Our database is temporarily unavailable. Please try again in a few moments.',
  
  // Subscription errors
  'Subscription not found': 'Unable to find your subscription. Please contact support.',
  'No active subscription found': 'You do not have a paid subscription yet. Complete checkout to subscribe.',
  'Payment failed': 'Your payment could not be processed. Please check your payment method.',
  'Card declined': 'Your card was declined. Please check your payment details or try a different card.',
  'Insufficient funds': 'Your payment could not be processed due to insufficient funds.',
  'Request failed with status code': 'We could not complete that request. Please try again or contact support.',
  
  // Content errors
  'Content not found': 'The requested content is not available.',
  'Access denied': 'You don\'t have access to this content. Please upgrade your subscription.',
  
  // Generic
  'An unexpected error occurred': 'Something unexpected happened. Please try again.',
};

/**
 * Converts a technical error message into a user-friendly message
 * @param {string|Error} error - The error message or Error object
 * @param {string} fallback - Fallback message if no match is found
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error, fallback = 'An unexpected error occurred. Please try again.') => {
  // Handle Error objects
  let errorMessage = '';
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error) {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else {
    return fallback;
  }

  // Normalize the error message (lowercase, trim)
  const normalizedMessage = errorMessage.trim();

  // Check for exact matches first
  if (ERROR_MESSAGES[normalizedMessage]) {
    return ERROR_MESSAGES[normalizedMessage];
  }

  // Check for partial matches (case-insensitive)
  for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
    if (normalizedMessage.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }

  // If it's already user-friendly (no technical jargon), return as-is
  // Check if it contains technical terms that suggest it's not user-friendly
  const technicalTerms = [
    'ECONNREFUSED', 'ENOTFOUND', 'ETIMEDOUT', 'MongoDB', 'Mongoose',
    'database', 'connection pool', 'socket', 'network', 'timeout',
    'stack trace', 'undefined', 'null reference', 'TypeError',
    'ReferenceError', 'SyntaxError', 'internal server error'
  ];

  const isTechnical = technicalTerms.some(term => 
    normalizedMessage.toLowerCase().includes(term.toLowerCase())
  );

  if (isTechnical) {
    return fallback;
  }

  // If it looks user-friendly, return it (e.g., validation errors)
  // Check if it's a validation-like message (short, clear, no technical jargon)
  if (normalizedMessage.length < 100 && 
      !normalizedMessage.includes('Error:') && 
      !normalizedMessage.includes('at ') &&
      !normalizedMessage.includes('Exception:')) {
    return normalizedMessage;
  }

  // Default to fallback for technical errors
  return fallback;
};

/**
 * Extracts error message from various error formats
 * @param {*} error - Error object, string, or response object
 * @returns {string} Extracted error message
 */
export const extractErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred.';

  // Handle strings
  if (typeof error === 'string') {
    return error;
  }

  // Prefer API response body over Axios generic "Request failed with status code NNN"
  if (error.response?.data) {
    // Handle validation errors - backend returns { error: "...", details: [...] }
    if (error.response.data.details && Array.isArray(error.response.data.details)) {
      const validationErrors = error.response.data.details
        .map(err => err.message || err.msg)
        .filter(Boolean)
        .join('. ');
      if (validationErrors) {
        return validationErrors;
      }
    }
    // Also check for errors array (alternative format)
    if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
      const validationErrors = error.response.data.errors
        .map(err => err.msg || err.message)
        .filter(Boolean)
        .join('. ');
      if (validationErrors) {
        return validationErrors;
      }
    }
    // Try multiple common error message locations
    if (typeof error.response.data === 'string' && error.response.data.trim()) {
      return error.response.data;
    }
    return error.response.data.error || 
           error.response.data.message || 
           error.response.data.errorMessage ||
           `Server error (${error.response.status})`;
  }

  // Handle request errors (no response)
  if (error.request) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  // Handle error objects with message property
  if (error.message) {
    if (/^Request failed with status code \d+$/i.test(error.message)) {
      return 'We could not complete that request. Please try again or contact support.';
    }
    return error.message;
  }

  return 'An unexpected error occurred.';
};

/**
 * Main function to get user-friendly error message
 * Combines extraction and translation
 */
export const getError = (error, fallback = 'An unexpected error occurred. Please try again.') => {
  const extractedMessage = extractErrorMessage(error);
  return getUserFriendlyErrorMessage(extractedMessage, fallback);
};

export default getError;

